import DOMPurify from 'dompurify';
import { marked } from 'marked';

marked.setOptions({
  breaks: false,
  gfm: true,
});

const MARKDOWN_SANITIZE_CONFIG: DOMPurify.Config = {
  USE_PROFILES: { html: true },
  ADD_ATTR: ['target', 'rel'],
};

/**
 * Encode each path segment so spaces and special chars work in markdown [text](url) links.
 */
export function encodeUrlPath(path: string): string {
  if (!path) {
    return path;
  }
  return path.split('/').map((segment) => {
    if (!segment) {
      return segment;
    }
    try {
      return encodeURIComponent(decodeURIComponent(segment));
    } catch {
      return encodeURIComponent(segment);
    }
  }).join('/');
}

function encodeMarkdownUrl(url: string): string {
  const trimmed = url.trim();
  const absolute = trimmed.match(/^([a-z][a-z0-9+.-]*:\/\/[^/]+)(\/[^?#]*)?(\?[^#]*)?(#.*)?$/i);
  if (absolute) {
    const base = absolute[1];
    const path = absolute[2] || '';
    const query = absolute[3] || '';
    const hash = absolute[4] || '';
    return base + encodeUrlPath(path) + query + hash;
  }
  const queryIndex = trimmed.indexOf('?');
  const hashIndex = trimmed.indexOf('#');
  const suffixIndex = queryIndex >= 0
    ? queryIndex
    : (hashIndex >= 0 ? hashIndex : -1);
  if (suffixIndex >= 0) {
    return encodeUrlPath(trimmed.slice(0, suffixIndex)) + trimmed.slice(suffixIndex);
  }
  return encodeUrlPath(trimmed);
}

/**
 * Markdown link URLs must not contain raw spaces or marked breaks the link.
 */
export function normalizeMarkdownLinks(markdown: string): string {
  return markdown.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_match, label: string, url: string) => `[${label}](${encodeMarkdownUrl(url)})`,
  );
}

let linkHooksRegistered = false;

function registerLinkSafetyHooks(): void {
  if (linkHooksRegistered) {
    return;
  }
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  linkHooksRegistered = true;
}

/**
 * Parse markdown and return HTML safe for [innerHTML] binding.
 */
export function parseAndSanitizeMarkdown(content: string): string {
  registerLinkSafetyHooks();
  const normalized = normalizeMarkdownLinks(content);
  const html = marked.parse(normalized) as string;
  return DOMPurify.sanitize(html, MARKDOWN_SANITIZE_CONFIG);
}

export function escapePlainTextForHtml(content: string): string {
  return content.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
